import PermissionButton from '../../permissions/PermissionButton';
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  message,
  Button,
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Space,
  Select,
  Input,
  Descriptions,
  Tag, 
  InputNumber,
  DatePicker
} from "antd";
import dayjs from 'dayjs';

import UnifiedFactureAvoirTransferList from "./UnifiedFactureAvoirTransferList";
import ReglementDetailsList from "./RegDetailsList";
import {
  useFetchAllUnifiedFactureAvoir,
  useFetchReglementById,
  useUpdateReglement,
  useSaveReglement,
  useCreateReglement,
  useCreateSurplus,
} from "../../hooks/regHooks";
import { SaveOutlined, CloseOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { usePaymentModes, useCities, useBanks } from '../../hooks/listsHooks';

import SurplusButtons from './SurplusButtons';

const { Text } = Typography;
const { Option } = Select;

// Helper function to determine the type based on REF_AV_FAC prefix
const getTransactionType = (ref) => {
    if (ref.startsWith('FA')) return 'FACTURE';
    if (ref.startsWith('AV')) return 'AVOIR';
    if (ref.startsWith('SUR')) return 'SURPLUS';
    return 'UNKNOWN';
};

const EditableField = ({ value, onChange, options, inputType = "input", prefix, min, max }) => {
  const handleChange = (e) => {
    const newValue = inputType === "select" ? e :
      inputType === "number" ? e :
      inputType === "date" ? e :
        e.target.value;
    onChange(newValue);
  };

  if (inputType === "number") {
    return (
      <InputNumber
        value={value}
        onChange={handleChange}
        style={{ width: '200px' }}
        prefix={prefix}
        min={min}
        max={max}
        precision={2}
      />
    );
  }

  if (inputType === "date") {
    return (
      <DatePicker
        value={value ? dayjs(value) : null}
        onChange={(date) => onChange(date ? date.toDate() : null)}
        style={{ width: '200px' }}
        format="DD/MM/YYYY"
      />
    );
  }

  return inputType === "select" ? (
    <Select
      value={value}
      onChange={handleChange}
      style={{ width: '200px' }}
    >
      {options.map(option => (
        <Option key={option} value={option}>{option}</Option>
      ))}
    </Select>
  ) : (
    <Input
      value={value}
      onChange={handleChange}
      style={{ width: '200px' }}
    />
  );
};

const SingleReglement = ({ reglementId, onClose, draftClient, onChangeView, onCompleted }) => {
  const {
    reglement: initialReglement,
    loading,
    error,
    refetch: fetchReglement,
  } = useFetchReglementById(reglementId);

  const [clientCode, setClientCode] = useState(null);
  const [localReglement, setLocalReglement] = useState(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const { handleUpdate } = useUpdateReglement();
  const { handleCreate, isCreating } = useCreateReglement();
  const { handleCreate: handleCreateSurplus, isCreating: isCreatingSurplus } = useCreateSurplus();

  const { 
    unifiedRecords = [], 
    loading: loadingUnified, 
    error: errorUnified,
    refetch: refetchUnified 
  } = useFetchAllUnifiedFactureAvoir(clientCode);

  const { handleSaveReglement, isSaved, error: saveError } = useSaveReglement();
  const [isSaving, setIsSaving] = useState(false); // New state for loading
  const [saveTrigger, setSaveTrigger] = useState(0); // New state for save trigger
  const { modes: paymentModes } = usePaymentModes();
  const { cities } = useCities();
  const { banks } = useBanks();

  useEffect(() => {
    if (initialReglement) {
      setLocalReglement({ ...initialReglement });
      // Set client code after setting local reglement
      setClientCode({
        code_clt: initialReglement.CODE_CLT.toString()
      });
    }
  }, [initialReglement]);

  // Initialize draft mode when a client is provided and no reglementId
  useEffect(() => {
    if (!reglementId && draftClient) {
      const codeClt = String(draftClient.code_clt || draftClient.CODE_CLT || '').trim();
      setLocalReglement({
        REF_REGV: 'NOUVEAU',
        DATEVALID: null,
        HEUREVALID: null,
        CODE_CLT: codeClt,
        CLIENT: draftClient.CLIENT,
        COMPTE: draftClient.COMPTE || null,
        REF_PIECE: null,
        DATE_REM: null,
        DATE_ECH: null,
        DATE_PNT: null,
        MODE_REG: null,
        MNT_REGLER: 0,
        BANQUE: null,
        IMPAYER: null,
        CPT_BNQ: null,
        REMARQUE: null,
        TIRER: null,
        VILLE: null,
        remainingAmount: 0,
        hasSurplus: false,
        DATE_REG: new Date(),
        reglementdetails: [],
        surplus: []
      });
      setClientCode({ code_clt: codeClt });
    }
  }, [reglementId, draftClient]);

  const isDraftMode = !reglementId && !!draftClient;

  // Recalculate totals whenever reglement details change
  const recalculateTotals = useCallback((currentReglement) => {
    // Factures subtract from payment, Avoirs and Surplus add to payment
    const facturesTotal = currentReglement.reglementdetails
      .filter(detail => detail.REF_AV_FAC.startsWith('FA'))
      .reduce((sum, detail) => sum + Math.abs(detail.MNT_REGLER || 0), 0);
    
    const creditsTotal = currentReglement.reglementdetails
      .filter(detail => detail.REF_AV_FAC.startsWith('AV') || detail.REF_AV_FAC.startsWith('SUR'))
      .reduce((sum, detail) => sum + Math.abs(detail.MNT_REGLER || 0), 0);
    
    // Remaining = Payment - Factures + Credits (Avoirs + Surplus)
    const remainingAmount = (currentReglement.MNT_REGLER || 0) - facturesTotal + creditsTotal;

    return {
      ...currentReglement,
      remainingAmount: remainingAmount
    };
  }, []);

  const handleFieldChange = (field, value) => {
    setLocalReglement(prev => {
      const updatedReglement = {
        ...prev,
        [field]: value
      };

      // If we're updating MNT_REGLER, recalculate totals
      if (field === 'MNT_REGLER') {
        return recalculateTotals(updatedReglement);
      }

      return updatedReglement;
    });
    setHasLocalChanges(true);
  };

  // Handle updates from the transfer list
  const handleUpdateDetails = useCallback((newDetails) => {
    setLocalReglement(prev => {
      const updatedReglement = {
        ...prev,
        reglementdetails: newDetails
      };
      return recalculateTotals(updatedReglement);
    });
    setHasLocalChanges(true);
  }, [recalculateTotals]);

  // Prepare reglement data before saving
  const prepareReglementForSave = (reglement) => {
    const { client, ...cleanReglement } = reglement;
    return {
      ...cleanReglement,
      CODE_CLT: String(cleanReglement.CODE_CLT), // Keep CODE_CLT as string (as required by schema)
      MNT_REGLER: parseFloat(cleanReglement.MNT_REGLER || 0),
      remainingAmount: parseFloat(reglement.remainingAmount || 0),
    };
  };

  const handleEncaisser = async () => {
    try {
      if (!localReglement?.CODE_CLT) {
        message.error("Veuillez sélectionner un client.");
        return;
      }
      if (!localReglement?.MODE_REG) {
        message.error("Veuillez sélectionner le mode de paiement.");
        return;
      }
      if (!localReglement?.MNT_REGLER || Number(localReglement.MNT_REGLER) <= 0) {
        message.error("Veuillez saisir un montant à encaisser (> 0).");
        return;
      }
      if (localReglement.remainingAmount < 0) {
        message.error("Montant restant négatif. Ajustez les montants sélectionnés.");
        return;
      }

      // 1) Create the base reglement
      const createPayload = {
        CODE_CLT: String(localReglement.CODE_CLT).trim(),
        CLIENT: localReglement.CLIENT,
        COMPTE: localReglement.COMPTE,
        REF_PIECE: localReglement.REF_PIECE || null,
        MODE_REG: localReglement.MODE_REG,
        MNT_REGLER: parseFloat(localReglement.MNT_REGLER || 0),
        BANQUE: localReglement.BANQUE || null,
        VILLE: localReglement.VILLE || null,
        REMARQUE: localReglement.REMARQUE || null,
        TIRER: localReglement.TIRER || null,
        DATE_REM: localReglement.DATE_REM || null,
        DATE_ECH: localReglement.DATE_ECH || null,
        DATE_PNT: localReglement.DATE_PNT || null,
        IMPAYER: localReglement.IMPAYER || null,
        CPT_BNQ: localReglement.CPT_BNQ || null
      };

      setIsSaving(true);
      const created = await handleCreate(createPayload);

      if (!created || !created.id) {
        throw new Error("Création du règlement échouée.");
      }

      // 2) Save details to the created reglement
      const savePayload = {
        reglement: {
          ...created
        },
        reglementdetails: (localReglement.reglementdetails || []).map(d => ({
          REF_REGV: created.REF_REGV,
          REF_AV_FAC: d.REF_AV_FAC,
          MNT_ORIGINAL: Math.abs(d.MNT_ORIGINAL || d.MNT_TTC || 0),
          MNT_REGLER: Math.abs(d.MNT_REGLER || 0),
          DATE_DOC: d.DATE_DOC || new Date()
        }))
      };

      const savedReglement = await handleSaveReglement(savePayload);
      
      // 3) Check if there's a remaining amount and create surplus automatically
      // Use the remainingAmount from the saved reglement (backend calculation)
      const finalRemainingAmount = savedReglement?.remainingAmount ?? localReglement.remainingAmount;
      if (finalRemainingAmount > 0) {
        try {
          const surplusData = {
            MNT_SURPLUS: finalRemainingAmount,
            CODE_CLT: String(localReglement.CODE_CLT).trim(),
            REF_REGV: created.REF_REGV,
            REMARQUE: `Surplus automatique créé lors de l'encaissement`
          };
          
          await handleCreateSurplus(surplusData);
          message.success(`Encaissement réalisé avec succès. Surplus de ${finalRemainingAmount.toFixed(2)} MAD créé automatiquement.`);
        } catch (surplusError) {
          console.error('Error creating surplus:', surplusError);
          message.warning(`Encaissement réalisé avec succès, mais échec de la création du surplus: ${surplusError.message}`);
        }
      } else {
        message.success("Encaissement réalisé avec succès.");
      }
      
      setIsSaving(false);

      // 4) Reset to new draft for the same client instead of navigating to view
      if (draftClient) {
        // Reset the local reglement to a new empty draft
        const codeClt = String(draftClient.code_clt || draftClient.CODE_CLT || localReglement.CODE_CLT).trim();
        setLocalReglement({
          REF_REGV: 'NOUVEAU',
          DATEVALID: null,
          HEUREVALID: null,
          CODE_CLT: codeClt,
          CLIENT: draftClient.CLIENT || localReglement.CLIENT,
          COMPTE: draftClient.COMPTE || localReglement.COMPTE || null,
          REF_PIECE: null,
          DATE_REM: null,
          DATE_ECH: null,
          DATE_PNT: null,
          MODE_REG: null,
          MNT_REGLER: 0,
          BANQUE: null,
          IMPAYER: null,
          CPT_BNQ: null,
          REMARQUE: null,
          TIRER: null,
          VILLE: null,
          remainingAmount: 0,
          hasSurplus: false,
          DATE_REG: new Date(),
          reglementdetails: [],
          surplus: []
        });
        setHasLocalChanges(false);
        // Refresh unified records to show updated amounts
        if (clientCode) {
          refetchUnified();
        }
      } else {
        // If no draftClient, navigate to view (fallback)
        if (onCompleted) {
          onCompleted(created.id);
        } else if (onChangeView) {
          onChangeView('view', created.id);
        }
      }
    } catch (e) {
      console.error(e);
      setIsSaving(false);
      message.error(e.message || "Échec de l'encaissement.");
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Check if remaining amount is negative
      if (localReglement.remainingAmount < 0) {
        message.error("Impossible d'enregistrer un règlement avec un montant restant négatif. Veuillez ajuster les montants.");
        return;
      }

      // Warning if not fully settled and no surplus - using the field directly
      if (!localReglement.hasSurplus && localReglement.remainingAmount !== 0) {
        message.warning("Le règlement n'est pas entièrement réglé. Veuillez vous assurer que tous les montants sont correctement appliqués.");
      }

      const dataToSave = {
        reglement: prepareReglementForSave(localReglement),
        reglementdetails: localReglement.reglementdetails
      };

      setIsSaving(true);
      await handleSaveReglement(dataToSave);
      setIsSaving(false);
      setHasLocalChanges(false);
      message.success("Règlement enregistré avec succès!");
      fetchReglement();
      refetchUnified();
      setSaveTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error saving reglement:", error);
      message.error(`Échec de l'enregistrement du règlement: ${error.message || error}`);
      setIsSaving(false);
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "Not provided";

  const calculateTotalRegistered = () => {
    return localReglement?.reglementdetails?.reduce(
      (total, detail) => total + Math.abs(detail.MNT_REGLER || 0),
      0
    ) || 0;
  };

  const handleRefetch = useCallback(() => {
    fetchReglement();
    if (clientCode) {
      refetchUnified();
    }
  }, [fetchReglement, refetchUnified, clientCode]);

  const getDescriptionItems = () => [
    {
      key: 'registrationDate',
      label: 'Date d\'Enregistrement',
      children: formatDate(localReglement.DATE_REG),
      span: 1,
    },
    {
      key: 'amount',
      label: 'Montant à Régler',
      children: (
        <EditableField
          value={localReglement.MNT_REGLER}
          onChange={(value) => handleFieldChange("MNT_REGLER", value)}
          inputType="number"
          prefix="MAD"
          min={0}
          max={999999999}
        />
      ),
      span: 1,
    },
    {
      key: 'paymentMode',
      label: 'Mode de Paiement',
      children: (
        <EditableField
          value={localReglement.MODE_REG}
          onChange={(value) => handleFieldChange("MODE_REG", value)}
          options={paymentModes}
          inputType="select"
        />
      ),
      span: 1,
    },
    {
      key: 'refPiece',
      label: 'Référence Pièce',
      children: (
        <EditableField
          value={localReglement.REF_PIECE}
          onChange={(value) => handleFieldChange("REF_PIECE", value)}
        />
      ),
      span: 1,
    },
    {
      key: 'tirer',
      label: 'Tirer',
      children: (
        <EditableField
          value={localReglement.TIRER}
          onChange={(value) => handleFieldChange("TIRER", value)}
        />
      ),
      span: 1,
    },
    {
      key: 'city',
      label: 'Ville Tire',
      children: (
        <EditableField
          value={localReglement.VILLE}
          onChange={(value) => handleFieldChange("VILLE", value)}
          options={cities}
          inputType="select"
        />
      ),
      span: 1,
    },
    {
      key: 'echeance',
      label: 'Échéance',
      children: (
        <EditableField
          value={localReglement.DATE_ECH}
          onChange={(value) => handleFieldChange("DATE_ECH", value)}
          inputType="date"
        />
      ),
      span: 1,
    },
    {
      key: 'bank',
      label: 'Banque',
      children: (
        <EditableField
          value={localReglement.BANQUE}
          onChange={(value) => handleFieldChange("BANQUE", value)}
          options={banks}
          inputType="select"
        />
      ),
      span: 1,
    },
    {
      key: 'remarks',
      label: 'Remarques',
      children: (
        <EditableField
          value={localReglement.REMARQUE}
          onChange={(value) => handleFieldChange("REMARQUE", value)}
        />
      ),
      span: 2,
    },
  ];

  

  if (loading) return <Spin tip="Chargement..." />;
  if (error) return <Alert message="Erreur" description={error} type="error" showIcon />;
  if (!localReglement) return <Alert message="Aucun règlement trouvé" type="info" showIcon />;
  if (clientCode && loadingUnified) return <Spin tip="Chargement des enregistrements unifiés..." />;
  if (errorUnified) return <Alert message="Erreur lors du chargement des enregistrements unifiés" description={errorUnified} type="error" showIcon />;

  return (
    <div>
       <Card
        title={
          <div>
            
            <div style={{ fontSize: '14px', color: '#666' }}>
              <Text>Client: </Text>
              <Text strong>{localReglement.CLIENT}</Text>
              {localReglement.COMPTE && (
                <>
                  <Text style={{ marginLeft: 16 }}>Compte: </Text>
                  <Text strong>{localReglement.COMPTE}</Text>
                </>
              )}
            </div>
          </div>
        }
        extra={
          <Space>
            {onChangeView && localReglement?.CODE_CLT && (
              <Button
                icon={<UnorderedListOutlined />}
                onClick={() => onChangeView('list', null, String(localReglement.CODE_CLT))}
              >
                Règlements du Client
              </Button>
            )}
            {onClose && (
              <Button
                icon={<CloseOutlined />}
                onClick={onClose}
              >
                Fermer
              </Button>
            )}
            {!isDraftMode && (
              <>
                <SurplusButtons 
                  reglement={localReglement} 
                  onSurplusChange={handleRefetch}
                  disabled={localReglement.hasSurplus}
                />
                <PermissionButton permissionModule="payments" permissionAction="update"
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveChanges}
                  disabled={!hasLocalChanges || localReglement.remainingAmount < 0 || localReglement.hasSurplus || isSaving}
                  loading={isSaving}
                  title={
                    localReglement.hasSurplus ? "Impossible de modifier un règlement avec surplus" :
                    localReglement.remainingAmount < 0 ? "Impossible d'enregistrer avec un montant restant négatif" : ""
                  }
                >
                  Enregistrer les Modifications
                </PermissionButton>
              </>
            )}
            {isDraftMode && (
              <PermissionButton permissionModule="payments" permissionAction="allocate"
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleEncaisser}
                disabled={isSaving || isCreating || isCreatingSurplus}
                loading={isSaving || isCreating || isCreatingSurplus}
              >
                Encaisser
              </PermissionButton>
            )}
          </Space>
        }
      >
        <div style={{ marginBottom: 8 }}>
          {isDraftMode ? (
            <>
              <Text strong>Nouveau Règlement</Text>
              <Tag color="gold" style={{ marginLeft: "8px" }}>
                Brouillon
              </Tag>
            </>
          ) : (
            <>
              <Text strong>Détails du Règlement - {localReglement.REF_REGV}</Text>
              <Tag color={localReglement.remainingAmount > 0 ? "blue" : "green"} style={{ marginLeft: "8px" }}>
                {localReglement.remainingAmount > 0 ? "En Attente" : "Entièrement Réglé"}
              </Tag>
              {localReglement.hasSurplus && (
                <Tag color="purple" style={{ marginLeft: "8px" }}>
                  Réglé avec Surplus
                </Tag>
              )}
            </>
          )}
        </div>
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          items={getDescriptionItems()}
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        />
      </Card>

      <Card title="Factures et Avoirs" style={{ marginTop: 20 }}>
        <Row gutter={16}>
          <Col span={24}>
          <UnifiedFactureAvoirTransferList
              unifiedRecords={unifiedRecords || []}
              selectedItems={localReglement.reglementdetails || []}
              onSelectChange={handleUpdateDetails}
              remainingAmount={localReglement.remainingAmount}
              hasSurplus={localReglement.hasSurplus}
              currentReglementRef={localReglement.REF_REGV}
              reglementAmount={localReglement.MNT_REGLER}
              surplusCreated={localReglement.surplus || []}
              saveTrigger={saveTrigger}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default SingleReglement;
